import React from "react";
import { Link } from "react-router-dom";
import { SEO_GAMES } from "./seoData";
import { GAMES } from "../arcade/games";
import "./seo.css";

// Long-form text section + Related Games strip rendered below each game.
// Solves Semrush's "low word count" and "thin content" flags, plus the
// "pages with only one internal link" notice via the cross-link strip.
export default function GameSEOContent({ gameId }) {
  const data = SEO_GAMES[gameId];
  if (!data || !data.content) return null;
  const { tagline, overview, howToPlay, features, faqs } = data.content;
  const related = GAMES.filter((g) => g.id !== gameId).slice(0, 4);

  // FAQ JSON-LD injection helps Google show rich snippets.
  const faqJsonLd =
    faqs && faqs.length
      ? {
          "@context": "https://schema.org",
          "@type": "FAQPage",
          mainEntity: faqs.map((f) => ({
            "@type": "Question",
            name: f.q,
            acceptedAnswer: { "@type": "Answer", text: f.a },
          })),
        }
      : null;

  return (
    <section className="seo-content" aria-label="Game information and related games">
      {faqJsonLd && (
        <script
          type="application/ld+json"
          dangerouslySetInnerHTML={{ __html: JSON.stringify(faqJsonLd) }}
        />
      )}

      <div className="seo-inner">
        {tagline && <p className="seo-tagline">{tagline}</p>}

        <h2 className="seo-h2">About this game</h2>
        <p className="seo-para">{overview}</p>

        {howToPlay && howToPlay.length > 0 && (
          <>
            <h2 className="seo-h2">How to play</h2>
            <ol className="seo-list">
              {howToPlay.map((step, i) => (
                <li key={i}>{step}</li>
              ))}
            </ol>
          </>
        )}

        {features && features.length > 0 && (
          <>
            <h2 className="seo-h2">Features</h2>
            <ul className="seo-list">
              {features.map((f, i) => (
                <li key={i}>{f}</li>
              ))}
            </ul>
          </>
        )}

        {faqs && faqs.length > 0 && (
          <>
            <h2 className="seo-h2">Frequently asked questions</h2>
            <dl className="seo-faqs">
              {faqs.map((f, i) => (
                <React.Fragment key={i}>
                  <dt>{f.q}</dt>
                  <dd>{f.a}</dd>
                </React.Fragment>
              ))}
            </dl>
          </>
        )}

        <h2 className="seo-h2">More games in Zombie Arcade</h2>
        <div className="seo-related">
          {related.map((g) => {
            const Icon = g.icon;
            return (
              <Link
                key={g.id}
                to={g.route}
                className="seo-related-card"
                style={{ "--accent": g.accent, "--accent2": g.accent2 }}
              >
                <span className="seo-related-icon" aria-hidden="true">
                  <Icon size={20} />
                </span>
                <span className="seo-related-text">
                  <b>{g.title}</b>
                  <em>{g.tagline}</em>
                </span>
                {g.status === "coming-soon" && (
                  <span className="seo-related-pill">Coming soon</span>
                )}
              </Link>
            );
          })}
        </div>
      </div>
    </section>
  );
}
